import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import "./Portfolio.css";

/**
 * Portfólio de Experiências — standalone corporate showcase.
 *
 * Deliberately renders WITHOUT the site's Header/Footer/WhatsApp/CTA components:
 * it's a business-card page delivered privately for meetings and B2B outreach.
 * Data comes from GET /api/portfolio (built from destination_ideas + portfolio.py).
 */

const TRANSPORT_LABEL = {
  rodoviario: "Ônibus-leito · Loja",
  aereo_nac: "Voo Nacional",
  aereo_int: "Voo Internacional",
};

const CORP_TYPE_META = {
  negocios: { label: "Negócios & Feiras", cls: "corp-negocios" },
  incentivo: { label: "Incentivo · Metas", cls: "corp-incentivo" },
  bleisure: { label: "Bleisure · Convenções", cls: "corp-bleisure" },
};

function formatBRL(v) {
  if (!v) return null;
  return "R$ " + v.toLocaleString("pt-BR");
}

const PRICE_BUCKETS = [
  { id: "all", label: "Todas as faixas" },
  { id: "5k", label: "Até R$ 5.000" },
  { id: "10k", label: "R$ 5-10.000" },
  { id: "premium", label: "Premium (10k+)" },
];

// Initial number of cards visible per section; user can expand with "Ver mais"
const INITIAL_VISIBLE = 8;

/**
 * SectorFilterChips — 5 sector chips + "Todos" for corporate segmentation.
 * Only shows chips for sectors that have at least 1 destination available.
 */
function SectorFilterChips({ labels, value, onChange, counts }) {
  const chips = [
    { id: "all", label: "Todos os setores", count: counts.all || 0 },
    ...Object.entries(labels).map(([id, label]) => ({
      id, label, count: counts[id] || 0,
    })).filter((c) => c.count > 0),
  ];
  return (
    <div className="p-sector-chips" data-testid="p-sector-chips">
      <div className="p-sector-chips-head">
        Viagens que Fazem Sentido para o seu Segmento
      </div>
      <div className="p-sector-chips-row">
        {chips.map((c) => (
          <button
            key={c.id}
            className={`p-sector-chip ${value === c.id ? "active" : ""}`}
            onClick={() => onChange(c.id)}
            data-testid={`p-sector-${c.id}`}
          >
            <span>{c.label}</span>
            <span className="p-sector-count">{c.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Case studies — 3 anonymized real examples used by the ROI block to prove impact.
 * Content is hand-crafted and safe to share publicly (anonymized company references).
 */
const CASE_STUDIES = [
  {
    id: "case-vendas",
    tag: "Incentivo · Vendas",
    company: "Rede varejista · Interior de SP",
    destination: "Porto de Galinhas (5 dias · 42 pax)",
    challenge: "Bater meta trimestral de +18% no ticket médio.",
    result: "+27% em vendas no trimestre da campanha",
    detail: "Retenção de 96% no time comercial nos 12 meses seguintes.",
  },
  {
    id: "case-tech",
    tag: "Convenção · Kick-off Anual",
    company: "Escritório de contabilidade · Interior de SP",
    destination: "Foz do Iguaçu (3 dias · 24 pax)",
    challenge: "Kick-off anual com apresentação do planejamento estratégico.",
    result: "94% de aprovação em pesquisa interna pós-evento",
    detail: "Ambiente inspirador aumentou engajamento nas metas do ano.",
  },
  {
    id: "case-conv",
    tag: "Convenção · Bleisure",
    company: "Cooperativa agro · Sul do Brasil",
    destination: "Balneário Camboriú (4 dias · 120 pax)",
    challenge: "Convenção anual + reconhecimento de liderança.",
    result: "NPS interno de 84 · +15% de engajamento",
    detail: "Eleita 'melhor convenção da história da cooperativa'.",
  },
];

function CaseStudiesBlock() {
  return (
    <div className="p-cases" data-testid="p-cases-block">
      <div className="p-cases-head">
        <span className="p-cases-eyebrow">◇ Últimos Cases</span>
      </div>
      <div className="p-cases-grid">
        {CASE_STUDIES.map((c) => (
          <div key={c.id} className="p-case" data-testid={`p-case-${c.id}`}>
            <div className="p-case-tag">{c.tag}</div>
            <div className="p-case-company">{c.company}</div>
            <div className="p-case-dest">{c.destination}</div>
            <div className="p-case-challenge">
              <span>Desafio:</span> {c.challenge}
            </div>
            <div className="p-case-result">
              <strong>{c.result}</strong>
            </div>
            <div className="p-case-detail">{c.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * MarcasParceiras — discreet, sophisticated wall of client logos.
 * Logos are rendered monochrome (grayscale + brightness) so all brand
 * colors normalize to a soft white on the dark bg. Subtle glow on hover.
 */
const PARCEIROS = [
  { name: "Ágil Contabilidade", logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/0efp98g1_agil-contabilidade.webp" },
  { name: "Accredito",           logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/dqpbyu48_accredito.jpg" },
  { name: "CDL Jundiaí",         logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/59ybjtw9_cdl-jundiai.jpg" },
  { name: "ACE Euclides",        logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/0d1jmq0j_ace-euclides-da-cunha-paulista.jpg" },
  { name: "Embrasi",             logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/ty43bksm_cropped-logo-embrasi-branco.jpg" },
  { name: "eVisas",              logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/yth21ogn_evisas.jpg" },
  { name: "Kuky",                logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/dtawb18i_kuky.jpg" },
  { name: "Ligado na Net",       logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/4uzkc0dh_ligadonanet.jpg" },
  { name: "M3 Tecnologia",       logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/xf0gpmeb_m3-tecnologia.jpeg" },
  { name: "Colibri Supermercado",logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/xerv9470_supermercado-colibri.jpg" },
];

function MarcasParceirasBlock() {
  return (
    <MarcasBlock
      id="parceiros"
      eyebrow="◈ Marcas que confiam na Orion"
      title="Clientes & Parceiros Corporativos"
      subtitle="Empresas e associações comerciais que caminham com a gente há anos — de contabilidades e supermercados a hubs de tecnologia e federações do comércio."
      logos={PARCEIROS.map((p) => ({ name: p.name, logo: p.logo }))}
    />
  );
}

/**
 * MarcasBlock — reusable version of the Portfolio's client marquee.
 * Same visual language (p-parceiros CSS), configurable content.
 * Exported so any page (Corporativo, Educacional, etc) can reuse it.
 *
 * Props:
 *   id:       anchor id (optional)
 *   eyebrow:  small kicker
 *   title:    section headline
 *   subtitle: paragraph below the headline
 *   logos:    Array<{ name: string; logo: string }>
 */
export function MarcasBlock({ id, eyebrow, title, subtitle, logos = [] }) {
  const doubled = [...logos, ...logos];
  return (
    <section id={id} className="p-parceiros" data-testid={`marcas-block-${id || "generic"}`}>
      <div className="p-parceiros-aura" aria-hidden />
      <div className="p-parceiros-inner">
        <div className="p-parceiros-head">
          <span className="p-parceiros-eyebrow">{eyebrow}</span>
          <h2 className="p-parceiros-title">{title}</h2>
          {subtitle && <p className="p-parceiros-sub">{subtitle}</p>}
        </div>
        <div className="p-parceiros-marquee">
          <div className="p-parceiros-track">
            {doubled.map((p, i) => (
              <div key={`${p.name}-${i}`} className="p-parceiro" title={p.name} aria-label={p.name}>
                <div className="p-parceiro-disc">
                  <img src={p.logo} alt={p.name} loading="lazy" decoding="async" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Hamburger Menu — floating anchor navigation for the portfolio.
 * Sits top-right, fixed, with expandable panel.
 */
function PortfolioHamburger({ onScrollTo }) {
  const [open, setOpen] = useState(false);
  const items = [
    { label: "Experiências",       target: "corporativos" }, // primeira seção
    { label: "Sobre Nós",           target: "orion" },
    { label: "Clientes",             target: "parceiros" },
    { label: "Viagem por Setor",     target: "corporativos" },
    { label: "Viagens com Filhos",   target: "filhos" },
    { label: "Viagens Românticas",   target: "romantica" },
    { label: "Viagens Corporativas", target: "corporativos" },
  ];
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <>
      <button
        className={`p-hamburger-btn ${open ? "active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Fechar menu" : "Abrir menu"}
        data-testid="p-hamburger"
      >
        <span /><span /><span />
      </button>
      {open && (
        <>
          <div className="p-hamburger-backdrop" onClick={() => setOpen(false)} />
          <nav className="p-hamburger-panel" data-testid="p-hamburger-panel">
            <div className="p-hamburger-head">Portfólio Orion</div>
            {items.map((it, i) => (
              <button
                key={i}
                className="p-hamburger-item"
                onClick={() => { onScrollTo(it.target); setOpen(false); }}
                data-testid={`p-hamburger-item-${i}`}
              >
                <span>{it.label}</span>
                <span className="p-hamburger-arrow">→</span>
              </button>
            ))}
          </nav>
        </>
      )}
    </>
  );
}

function DestinationCard({ item, clientMode, isSaved, onToggleSave, showCorpBadge, showCorpSummary }) {
  const preferido = item.preferido;
  const corp = showCorpBadge && item.corp_type ? CORP_TYPE_META[item.corp_type] : null;
  return (
    <article
      className={`p-card ${preferido ? "is-preferido" : ""} ${clientMode ? "client-mode" : ""} ${corp ? "is-corp" : ""}`}
      data-testid={`p-card-${item.id}`}
    >
      <button
        className={`p-save-btn ${isSaved ? "saved" : ""}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave?.(item.id); }}
        title={isSaved ? "Remover da minha lista" : "Adicionar à minha lista"}
        aria-label="Salvar"
        data-testid={`p-save-${item.id}`}
      >
        {isSaved ? "★" : "☆"}
      </button>
      <div className="p-card-media">
        <div className="p-card-media-tags">
          <span className="p-tag p-tag-days">{item.days === 1 ? "Bate-volta" : `${item.days} dias`}</span>
          {preferido && <span className="p-tag p-tag-preferido">Preferido</span>}
          {corp && <span className={`p-tag p-tag-corp ${corp.cls}`}>{corp.label}</span>}
          {!showCorpBadge && item.tags?.includes("filhos") && <span className="p-tag p-tag-filhos">Filhos</span>}
          {!showCorpBadge && item.tags?.includes("romantica") && <span className="p-tag p-tag-romantica">Romântica</span>}
        </div>
        <img src={item.image} alt={item.name} loading="lazy" decoding="async" />
      </div>
      <div className="p-card-body">
        <div className="p-card-region">{item.region?.replace("-", " · ")}</div>
        <h3 className="p-card-title">{item.name.replace(/\s+[A-Z]{2}\s*$/, "")}</h3>
        {showCorpSummary && item.corp_summary ? (
          <>
            <p className="p-card-corp-summary" data-testid={`p-corp-summary-${item.id}`}>
              {item.corp_summary}
            </p>
            {item.corp_reason && (
              <div className="p-card-corp-reason" title="Por que este destino é corporativo">
                <span className="p-corp-reason-label">Por quê?</span>
                <span className="p-corp-reason-text">{item.corp_reason}</span>
              </div>
            )}
          </>
        ) : (
          <p className="p-card-pitch">{item.pitch}</p>
        )}
        {item.highlights?.length > 0 && (
          <div className="p-card-highlights">
            {item.highlights.slice(0, 4).map((h, i) => (
              <span className="p-hi-chip" key={i}>{h}</span>
            ))}
          </div>
        )}
        <div className="p-card-footer">
          <div className="p-card-price">
            {item.price_from ? (
              <>
                <span>A partir de{item.price_estimated ? " (est.)" : ""}</span>
                <strong>{formatBRL(item.price_from)}</strong>
              </>
            ) : (
              <>
                <span>Roteiro sob</span>
                <strong>consulta</strong>
              </>
            )}
          </div>
          <span className="p-card-transport">{TRANSPORT_LABEL[item.transport]}</span>
        </div>
      </div>
    </article>
  );
}

/**
 * ROI Block — educational, transparent overview of why corporate travel pays off.
 * Rendered inside the Rotas Corporativas section head. Discreet, gold/cyan aesthetic.
 */
function CorporateROIBlock({ onQuote }) {
  return (
    <div className="p-roi" data-testid="p-roi-block">
      <div className="p-roi-intro">
        <strong>Transparência sobre ROI.</strong> Reunimos aqui os destinos que fazem
        sentido para times de vendas, diretorias e funcionários em geral — sempre com
        justificativa objetiva do porquê cada um entra nesta curadoria. A ideia é que
        você (e seu cliente) entendam onde o investimento se converte em resultado.
      </div>
      <div className="p-roi-grid">
        <div className="p-roi-card">
          <div className="p-roi-eyebrow">📈 Viagens de Incentivo</div>
          <h4 className="p-roi-title">Premiação e Metas</h4>
          <ul className="p-roi-list">
            <li><strong>Alavancagem de faturamento:</strong> a meta agressiva paga a viagem — e sobra lucro.</li>
            <li><strong>Retenção de talentos:</strong> reduz turnover (substituir um talento qualificado custa caro).</li>
            <li><strong>Engajamento:</strong> quem volta motivado impacta clima e produtividade do time.</li>
          </ul>
        </div>
        <div className="p-roi-card">
          <div className="p-roi-eyebrow">🤝 Negócios & Convenções</div>
          <h4 className="p-roi-title">Feiras, Missões e Bleisure</h4>
          <ul className="p-roi-list">
            <li><strong>Fechamento presencial:</strong> reunião olho-no-olho tem taxa de conversão maior que videoconferência.</li>
            <li><strong>Prospecção qualificada:</strong> estar no polo do seu setor abre pipeline futuro.</li>
            <li><strong>Treinamento e inovação:</strong> conhecimento técnico traz novos produtos e otimizações.</li>
          </ul>
        </div>
      </div>
      <div className="p-roi-example">
        <div className="p-roi-example-eyebrow">Exemplo prático</div>
        <div className="p-roi-example-grid">
          <div>
            <strong>Destino de Incentivo</strong> (ex: Porto de Galinhas)
            <span>Métrica: % de retenção do time e crescimento de vendas no trimestre da campanha.</span>
          </div>
          <div>
            <strong>Destino de Negócios</strong> (ex: Lisboa · Web Summit)
            <span>Métrica: nº de contratos fechados e valor em pipeline gerado no evento.</span>
          </div>
        </div>
      </div>
      <div className="p-group-discount" data-testid="p-group-discount-roi">
        <span className="p-gd-icon">%</span>
        <span className="p-gd-text">
          <strong>Grupos a partir de 10 pessoas:</strong> os valores estimados dos pacotes
          podem ter <em>até 60% de desconto</em>.
        </span>
      </div>
      <p className="p-roi-tip">
        💡 Cada card abaixo tem um selo de tipo (Negócios · Incentivo · Bleisure) e uma
        justificativa curta explicando <em>por quê aquele destino</em> entra na nossa
        curadoria corporativa.
      </p>
      <CaseStudiesBlock />
      <div className="p-roi-cta">
        <button
          className="p-roi-cta-btn"
          onClick={() => onQuote?.({ focusCorp: true })}
          data-testid="p-roi-cta-quote"
        >
          ✉ Solicitar Cotação Corporativa
        </button>
        <span className="p-roi-cta-hint">
          Resposta em até 24h · Nossa curadoria monta o roteiro sob medida
        </span>
      </div>
    </div>
  );
}

export default function Portfolio() {
  usePageMeta(
    "Viagens Corporativas! Incentive seu Time a Crescer | Viagens Orion",
    "Proporcione experiências únicas para sua equipe. Roteiros sob medida com suporte completo, bilhetes aéreos e serviços exclusivos.",
    {
      image: "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/isnd35rc_Gemini_Generated_Image_cgawhccgawhccgaw.jpg",
      ogDescription: "Mais que viagens: conhecimento, motivação e resultados para a sua empresa. Solicite uma cotação personalizada!",
      canonical: "https://viagensorion.com.br/viagens-corporativas/portfolio",
    }
  );

  const [sections, setSections] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState("");
  const [bgImages, setBgImages] = useState([]);
  const [bgIdx, setBgIdx] = useState(0);
  const [founders, setFounders] = useState([]);
  // Deep-linking: read initial state from URL (?filtro=5k&modo=cliente&salvos=id1,id2#hash)
  const initialUrl = useMemo(() => {
    if (typeof window === "undefined") return { filtro: "all", modo: false, hash: "", salvos: [] };
    const params = new URLSearchParams(window.location.search);
    const filtro = params.get("filtro");
    const modo = params.get("modo") === "cliente";
    const hash = (window.location.hash || "").replace(/^#/, "");
    const salvosParam = params.get("salvos");
    const salvos = salvosParam ? salvosParam.split(",").filter(Boolean) : null;
    return {
      filtro: ["5k", "10k", "premium", "all"].includes(filtro) ? filtro : "all",
      modo,
      hash,
      salvos, // null = ignore, [] or [...] = override
    };
  }, []);
  const [priceBucket, setPriceBucket] = useState(initialUrl.filtro);
  const [clientMode, setClientMode] = useState(initialUrl.modo);
  // Saved destinations (localStorage persistence, with URL override)
  const [savedIds, setSavedIds] = useState(() => {
    if (initialUrl.salvos && initialUrl.salvos.length) return initialUrl.salvos;
    if (typeof window === "undefined") return [];
    try { return JSON.parse(localStorage.getItem("orion_portfolio_saved") || "[]"); }
    catch { return []; }
  });
  const [savedOnly, setSavedOnly] = useState(!!(initialUrl.salvos && initialUrl.salvos.length));
  const [showQR, setShowQR] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [presIdx, setPresIdx] = useState(-1);
  // Load-more per section — remembers how many cards are visible per section id
  const [visibleCounts, setVisibleCounts] = useState({});
  // Corporate Quote Modal
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteInitial, setQuoteInitial] = useState({ preselect: null });
  // Sector filter (only applies to Rotas Corporativas)
  const [sectorFilter, setSectorFilter] = useState("all");
  const [sectorLabels, setSectorLabels] = useState({});
  const observerRef = useRef(null);

  const showMoreFor = useCallback((id, total) => {
    setVisibleCounts((prev) => {
      const cur = prev[id] || INITIAL_VISIBLE;
      return { ...prev, [id]: Math.min(cur + INITIAL_VISIBLE, total) };
    });
  }, []);
  const collapseSection = useCallback((id) => {
    setVisibleCounts((prev) => ({ ...prev, [id]: INITIAL_VISIBLE }));
  }, []);

  // Flat list of every destination (deduped by id) — used by the Quote modal picker
  const allDestinations = useMemo(() => {
    const map = new Map();
    for (const s of sections) {
      for (const it of s.items) if (!map.has(it.id)) map.set(it.id, it);
    }
    return Array.from(map.values());
  }, [sections]);

  // Sector counts, computed from the "corporativos" section (unfiltered) — used by chips
  const sectorCounts = useMemo(() => {
    const corp = sections.find((s) => s.id === "corporativos");
    if (!corp) return {};
    const c = { all: corp.items.length };
    for (const it of corp.items) {
      for (const s of it.corp_sectors || []) c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [sections]);

  const openQuote = useCallback((opts = {}) => {
    setQuoteInitial({
      preselectIds: opts.preselectIds || [],
      focusCorp: !!opts.focusCorp,
    });
    setQuoteOpen(true);
  }, []);

  const toggleSave = useCallback((id) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem("orion_portfolio_saved", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  const savedSet = useMemo(() => new Set(savedIds), [savedIds]);

  // Denormalized saved items (for the summary bar + WhatsApp share)
  const savedItems = useMemo(() => {
    if (!savedIds.length) return [];
    const map = new Map();
    for (const s of sections) for (const it of s.items) if (savedSet.has(it.id)) map.set(it.id, it);
    return Array.from(map.values());
  }, [sections, savedIds, savedSet]);

  const clearAllSaved = useCallback(() => {
    if (!window.confirm(`Remover todos os ${savedIds.length} destinos salvos?`)) return;
    setSavedIds([]);
    setSavedOnly(false);
    try { localStorage.removeItem("orion_portfolio_saved"); } catch {}
  }, [savedIds.length]);

  const shareOnWhatsApp = useCallback(() => {
    if (!savedItems.length) return;
    // Build a rich WhatsApp message summarizing the selection
    const origin = window.location.origin;
    const params = new URLSearchParams({ salvos: savedIds.join(",") });
    const shareUrl = `${origin}/viagens-corporativas/portfolio?${params.toString()}`;
    const lines = savedItems.map((it) => {
      const name = it.name.replace(/\s+[A-Z]{2}\s*$/, "");
      const price = it.price_from
        ? `— A partir de R$ ${it.price_from.toLocaleString("pt-BR")}${it.price_estimated ? " (est.)" : ""}`
        : "— Sob consulta";
      const dur = it.days === 1 ? "Bate-volta" : `${it.days} dias`;
      return `• ${name} · ${dur} ${price}`;
    });
    const text = [
      "Olá, Viagens Orion!",
      "",
      "*Seleção do Portfólio*",
      `*Total de destinos:* ${savedItems.length}`,
      "",
      "*Destinos selecionados:*",
      ...lines,
      "",
      `*Inclusos:* Transfer IN/OUT + motorista bilíngue + seguro-viagem${
        savedItems.some((i) => i.days >= 2) ? " + hospedagem bem localizada" : ""
      }`,
      "",
      `*Link da seleção:* ${shareUrl}`,
    ].join("\n");
    const whatsappNumber = "5511920043024"; // Orion WhatsApp
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  }, [savedItems, savedIds]);

  const copySavedLink = useCallback(() => {
    if (!savedIds.length) return;
    const origin = window.location.origin;
    const params = new URLSearchParams({ salvos: savedIds.join(",") });
    const url = `${origin}/viagens-corporativas/portfolio?${params.toString()}`;
    navigator.clipboard?.writeText(url).then(() => {
      // simple visual feedback via alert (fine for corporate use)
      alert("Link copiado! Compartilhe onde quiser — a seleção fica salva.");
    });
  }, [savedIds]);

  // Filtered sections by price bucket AND saved-only AND sector (memoized)
  const filteredSections = useMemo(() => {
    let secs = sections;
    if (savedOnly) {
      // Special: collapse into a single "Destinos Salvos" pseudo-section
      const items = [];
      const seen = new Set();
      for (const s of secs) {
        for (const it of s.items) {
          if (savedSet.has(it.id) && !seen.has(it.id)) {
            seen.add(it.id);
            items.push(it);
          }
        }
      }
      return items.length ? [{ id: "salvos", title: "Minha Lista", count: items.length, items }] : [];
    }
    // Apply sector filter ONLY to the "corporativos" section (does not affect others)
    if (sectorFilter !== "all") {
      secs = secs.map((s) => {
        if (s.id !== "corporativos") return s;
        const items = s.items.filter((i) => (i.corp_sectors || []).includes(sectorFilter));
        return { ...s, items, count: items.length };
      }).filter((s) => s.id !== "corporativos" || s.items.length > 0);
    }
    if (priceBucket === "all") return secs;
    return secs
      .map((s) => ({ ...s, items: s.items.filter((i) => i.price_bucket === priceBucket) }))
      .filter((s) => s.items.length > 0)
      .map((s) => ({ ...s, count: s.items.length }));
  }, [sections, priceBucket, savedOnly, savedSet, sectorFilter]);

  // Fetch portfolio + about (for founders block)
  useEffect(() => {
    api.get("/portfolio").then((r) => {
      const secs = r.data?.sections || [];
      setSections(secs);
      setTotal(r.data?.total_destinations || 0);
      setSectorLabels(r.data?.sector_labels || {});
      // Collect 8 random background images from the destinations for the hero rotation
      const all = secs.flatMap((s) => s.items.map((i) => i.image)).filter(Boolean);
      const shuffled = [...new Set(all)].sort(() => 0.5 - Math.random()).slice(0, 8);
      setBgImages(shuffled);
    }).catch(() => {}).finally(() => setLoading(false));

    api.get("/about").then((r) => {
      const authors = r.data?.authors || [];
      const sandra = authors.find((a) => a.slug === "sandra-scabio");
      const lucas = authors.find((a) => a.slug === "lucas-scabio");
      setFounders([sandra, lucas].filter(Boolean));
    }).catch(() => {});
  }, []);

  // Rotate background images every 6s
  useEffect(() => {
    if (bgImages.length < 2) return;
    const t = setInterval(() => setBgIdx((i) => (i + 1) % bgImages.length), 6000);
    return () => clearInterval(t);
  }, [bgImages]);

  // Hide all global chrome widgets (cookie consent, chat bubbles, iframes) inside portfolio
  useEffect(() => {
    document.body.classList.add("portfolio-page");
    const kill = () => {
      const selectors = [
        ".adopt-consent-notification",
        "#adopt-consent-notification",
        "[class*='adopt-']",
        "[id*='adopt']",
      ];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          if (!el.closest(".portfolio-root")) {
            el.style.display = "none";
          }
        });
      });
    };
    kill();
    const t = setInterval(kill, 800);
    const stop = setTimeout(() => clearInterval(t), 8000);
    return () => {
      clearInterval(t);
      clearTimeout(stop);
      document.body.classList.remove("portfolio-page");
    };
  }, []);

  // IntersectionObserver to highlight active anchor
  useEffect(() => {
    if (!sections.length) return;
    if (observerRef.current) observerRef.current.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActiveId(e.target.id);
            // Update URL hash so this section is shareable
            if (window.location.hash !== "#" + e.target.id) {
              const p = window.location.pathname + window.location.search;
              window.history.replaceState(null, "", p + "#" + e.target.id);
            }
          }
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    // Also observe extra sections outside the corpus loop
    const extras = ["parceiros", "orion"];
    for (const id of extras) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Presentation mode navigation
  const openPresentation = useCallback(() => {
    if (filteredSections.length > 0) setPresIdx(0);
  }, [filteredSections.length]);
  const closePresentation = useCallback(() => setPresIdx(-1), []);
  const nextSlide = useCallback(() => {
    setPresIdx((i) => (i < filteredSections.length - 1 ? i + 1 : i));
  }, [filteredSections.length]);
  const prevSlide = useCallback(() => {
    setPresIdx((i) => (i > 0 ? i - 1 : i));
  }, []);

  // Global keyboard shortcuts: F (toggle presentation), ← → (nav), Esc (close)
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea"].includes(tag)) return;
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        if (presIdx >= 0) closePresentation();
        else openPresentation();
      } else if (e.key === "Escape") {
        if (presIdx >= 0) closePresentation();
        if (showQR) setShowQR(false);
        if (showDisclaimer) setShowDisclaimer(false);
      } else if (presIdx >= 0 && (e.key === "ArrowRight" || e.key === " ")) {
        e.preventDefault();
        nextSlide();
      } else if (presIdx >= 0 && e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [presIdx, showQR, showDisclaimer, openPresentation, closePresentation, nextSlide, prevSlide]);

  // Sync URL with current filter/client-mode/saved state (replaceState, no history spam)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (priceBucket !== "all") params.set("filtro", priceBucket);
    else params.delete("filtro");
    if (clientMode) params.set("modo", "cliente");
    else params.delete("modo");
    // Only include savedIds when user is filtering by saved (shareable)
    if (savedOnly && savedIds.length) params.set("salvos", savedIds.join(","));
    else params.delete("salvos");
    const query = params.toString();
    const url = window.location.pathname + (query ? "?" + query : "") + window.location.hash;
    window.history.replaceState(null, "", url);
  }, [priceBucket, clientMode, savedOnly, savedIds]);

  // On first section load, scroll to hash if present (?...#nac-praias)
  useEffect(() => {
    if (loading || !initialUrl.hash) return;
    const el = document.getElementById(initialUrl.hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
  }, [loading, initialUrl.hash]);

  // Portfolio URL for QR
  const portfolioUrl = typeof window !== "undefined" ? window.location.origin + "/viagens-corporativas/portfolio" : "";

  const downloadQR = () => {
    const svg = document.querySelector(".p-qr-wrap svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    const size = 1200;
    canvas.width = size;
    canvas.height = size;
    img.onload = () => {
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement("a");
      a.download = "portfolio-orion-qr.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const openPrintPDF = () => {
    window.open("/viagens-corporativas/portfolio/imprimir", "_blank", "noopener");
  };

  return (
    <div className="portfolio-root" data-testid="portfolio-root">
      <div className="portfolio-noise" aria-hidden />
      <PortfolioHamburger onScrollTo={scrollTo} />


      <div className="portfolio-content">
        {/* HERO — interactive index with rotating background images */}
        <header className="p-hero" data-testid="p-hero">
          <div className="p-hero-bg" aria-hidden>
            {bgImages.map((url, i) => (
              <img
                key={url}
                src={url}
                alt=""
                loading={i === 0 ? "eager" : "lazy"}
                className={`p-hero-bg-img ${i === bgIdx ? "active" : ""}`}
              />
            ))}
          </div>

          <div className="p-hero-inner">
            <div className="p-hero-top">
              <div className="p-hero-text-block">
                <div className="p-hero-logo" data-testid="p-hero-logo">
                  <img
                    src="https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/ym5imeh2_logo%20pnr%20branca.png"
                    alt="Viagens Orion — Agência e Operadora de Turismo"
                    loading="eager"
                  />
                </div>
                <div className="p-hero-eyebrow">✦ Viagens Orion · Roteiros 2026</div>
                <h1 className="p-hero-title">
                  Portfólio de <em>Experiências</em>
                </h1>
                <p className="p-hero-sub">
                  {total > 0 ? `${total} destinos ` : ""}desenhados sob medida.
                  Toda experiência inclui Transfer IN/OUT com motorista bilíngue,
                  seguro-viagem e hospedagem bem localizada.
                </p>

                {/* Price filter — hidden in Client Mode to avoid revealing prices in the pills */}
                {!clientMode && (
                  <div className="p-price-filter" data-testid="p-price-filter">
                  <div className="p-price-pills">
                    {PRICE_BUCKETS.map((b) => (
                      <button
                        key={b.id}
                        className={`p-tool-btn ${!savedOnly && priceBucket === b.id ? "active" : ""}`}
                        onClick={() => { setPriceBucket(b.id); setSavedOnly(false); }}
                        data-testid={`p-price-${b.id}`}
                      >
                        {b.label}
                      </button>
                    ))}
                    {savedIds.length > 0 && (
                      <button
                        className={`p-tool-btn warn ${savedOnly ? "active" : ""}`}
                        onClick={() => setSavedOnly((v) => !v)}
                        data-testid="p-filter-saved"
                      >
                        ★ Minha Lista ({savedIds.length})
                      </button>
                    )}
                  </div>
                  <div className="p-price-note">
                    * Valores em média por pessoa.{" "}
                    <button
                      onClick={() => setShowDisclaimer(true)}
                      style={{ background: "transparent", border: 0, color: "var(--p-cyan)", cursor: "pointer", padding: 0, textDecoration: "underline", font: "inherit" }}
                      data-testid="p-price-disclaimer-open"
                    >
                      O que está incluso?
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Interactive index — the centerpiece */}
            <div className="p-index">
              <div className="p-index-label">
                <span>Categorias</span>
                <span>Clique para explorar</span>
              </div>
              {filteredSections.map((s) => {
                const [main, vibe] = s.title.includes(" · ") ? s.title.split(" · ") : [s.title, null];
                return (
                  <button
                    key={s.id}
                    className="p-index-row"
                    onClick={() => scrollTo(s.id)}
                    data-testid={`p-index-${s.id}`}
                  >
                    <span className="p-index-name">
                      {main}
                      {vibe && <em> · {vibe}</em>}
                    </span>
                    <span className="p-index-side">
                      <span className="p-index-count">{s.count}</span>
                      <span className="p-index-arrow">→</span>
                    </span>
                  </button>
                );
              })}
              {filteredSections.length === 0 && !loading && (
                <div style={{ padding: "40px 0", color: "var(--p-text-dim)" }}>
                  Nenhuma experiência encontrada nessa faixa. <button onClick={() => setPriceBucket("all")} style={{ background: "transparent", color: "var(--p-cyan)", border: 0, cursor: "pointer", textDecoration: "underline" }}>Mostrar todas</button>
                </div>
              )}
            </div>

            {/* Presentation toolbar — moved to below the index list */}
            <div className="p-toolbar p-toolbar-bottom" data-testid="p-toolbar">
              <button
                className={`p-tool-btn warn ${clientMode ? "active" : ""}`}
                onClick={() => setClientMode((v) => !v)}
                title="Ocultar valores para apresentar ao cliente sem revelar preços"
                data-testid="p-toolbar-client"
              >
                {clientMode ? "◉ Modo Cliente ON" : "◎ Modo Cliente"}
              </button>
              <button
                className={`p-tool-btn ${presIdx >= 0 ? "active" : ""}`}
                onClick={openPresentation}
                title="Apresentação fullscreen (tecla F)"
                data-testid="p-toolbar-presentation"
              >
                ◐ Apresentar (F)
              </button>
              <button
                className="p-tool-btn"
                onClick={() => setShowQR(true)}
                title="QR Code para reuniões"
                data-testid="p-toolbar-qr"
              >
                ▦ QR Code
              </button>
              <button
                className="p-tool-btn"
                onClick={openPrintPDF}
                title="Versão para imprimir/PDF"
                data-testid="p-toolbar-pdf"
              >
                ⇩ Baixar Catálogo
              </button>
            </div>

            {/* Discreet favorite hint — right before content starts */}
            <div className="p-fav-hint" data-testid="p-fav-hint">
              ★ Favorite as viagens que interessar<sup>*</sup>
            </div>
          </div>
        </header>

        {/* MARCAS PARCEIRAS — client logos wall */}
        <MarcasParceirasBlock />

        {/* INSTITUTIONAL BLOCK — Orion team presentation */}
        <section className="p-institutional" id="orion" data-testid="p-institutional">
          <div className="p-inst-grid">
            <div className="p-inst-text">
              <div className="p-inst-eyebrow">Sobre nós</div>
              <h2 className="p-inst-title">
                Uma agência que <em>caminha junto</em> com quem viaja.
              </h2>
              <p className="p-inst-body">
                A Viagens Orion nasceu do desejo de oferecer viagens com propósito, curadoria
                humana e o cuidado de uma família. Somos uma agência Cadastur com sede em São
                Paulo, especializada em roteiros nacionais e internacionais com Transfer IN/OUT,
                motorista bilíngue, seguro-viagem e hospedagem em áreas seguras e bem localizadas.
                Cada destino aqui foi pisado, testado e aprovado pela nossa equipe.
              </p>
              <div className="p-inst-stats">
                <div className="p-inst-stat">
                  <strong>+9</strong>
                  <span>anos de estrada</span>
                </div>
                <div className="p-inst-stat">
                  <strong>+11mil</strong>
                  <span>viajantes felizes</span>
                </div>
                <div className="p-inst-stat">
                  <strong>112</strong>
                  <span>destinos imperdíveis</span>
                </div>
              </div>
            </div>
            <div className="p-inst-images">
              <div className="p-inst-img hero-team">
                <img
                  src="https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/a6d58qvy_equipe%20orion.jpeg"
                  alt="Equipe Viagens Orion"
                  loading="lazy"
                  decoding="async"
                  width="860"
                  height="620"
                />
              </div>
              <div className="p-inst-thumbs">
                <div className="p-inst-img">
                  <img
                    src="https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/bjwmaet3_WhatsApp%20Image%202025-05-28%20at%2008.26.25%20-%20Copia%20%282%29.webp"
                    alt="Grupo Orion no Dreams Ice Bar"
                    loading="lazy"
                    decoding="async"
                    width="420"
                    height="300"
                  />
                </div>
                <div className="p-inst-img">
                  <img
                    src="https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/f7mxs5sb_chile%20%283%29.jpg"
                    alt="Grupo Orion em Farellones, Chile"
                    loading="lazy"
                    decoding="async"
                    width="420"
                    height="300"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTIONS */}
        {loading ? (
          <div style={{ padding: "80px 24px", textAlign: "center", color: "var(--p-text-dim)" }}>
            Carregando curadoria…
          </div>
        ) : (
          filteredSections.map((section) => {
            const isCorp = section.id === "corporativos";
            const visible = visibleCounts[section.id] || INITIAL_VISIBLE;
            const shown = section.items.slice(0, visible);
            const remaining = section.items.length - shown.length;
            return (
              <section
                id={section.id}
                key={section.id}
                className={`p-section ${isCorp ? "p-section-corp" : ""}`}
                data-testid={`p-section-${section.id}`}
              >
                <div className="p-section-head">
                  <div className="p-section-count">
                    <strong>{section.count}</strong> {section.count === 1 ? "experiência" : "experiências"}
                  </div>
                  <h2 className="p-section-title">
                    {isCorp && <span className="p-corp-seal">◈ Corporativo</span>}
                    {section.title}
                  </h2>
                  {isCorp && (
                    <>
                      <CorporateROIBlock onQuote={openQuote} />
                      <SectorFilterChips
                        labels={sectorLabels}
                        value={sectorFilter}
                        onChange={setSectorFilter}
                        counts={sectorCounts}
                      />
                    </>
                  )}
                </div>
                <div className={`p-grid ${isCorp ? "p-grid-corp" : ""}`}>
                  {shown.map((it) => (
                    <DestinationCard
                      key={`${section.id}-${it.id}`}
                      item={it}
                      clientMode={clientMode}
                      isSaved={savedSet.has(it.id)}
                      onToggleSave={toggleSave}
                      showCorpBadge={isCorp}
                      showCorpSummary={isCorp}
                    />
                  ))}
                </div>
                {section.items.length > INITIAL_VISIBLE && (
                  <div className="p-load-more">
                    {remaining > 0 ? (
                      <button
                        className="p-load-btn"
                        onClick={() => showMoreFor(section.id, section.items.length)}
                        data-testid={`p-load-more-${section.id}`}
                      >
                        Ver mais {Math.min(remaining, INITIAL_VISIBLE)} de {remaining} destinos restantes ↓
                      </button>
                    ) : (
                      <button
                        className="p-load-btn ghost"
                        onClick={() => collapseSection(section.id)}
                        data-testid={`p-collapse-${section.id}`}
                      >
                        Ver menos ↑
                      </button>
                    )}
                  </div>
                )}
              </section>
            );
          })
        )}

        {/* FOUNDERS / CURADORIA */}
        {founders.length > 0 && (
          <section id="curadoria" className="p-founders" data-testid="p-founders">
            <div className="p-founders-head">
              <span className="p-founders-eyebrow">Roteiros · Sócios</span>
              <h2 className="p-founders-title">
                Cada Roteiro é Elaborado por Profissionais Capacitados.
              </h2>
            </div>
            <div className="p-founders-grid">
              {founders.map((f) => (
                <div key={f.slug} className="p-founder" data-testid={`p-founder-${f.slug}`}>
                  {f.photo_url && (
                    <div className="p-founder-photo">
                      <img
                        src={f.photo_url}
                        alt={f.name}
                        loading="lazy"
                        style={{ objectPosition: f.photo_position || "center" }}
                      />
                    </div>
                  )}
                  <div>
                    <div className="p-founder-role">{f.short_role || "Sócio"}</div>
                    <h3 className="p-founder-name">{f.name}</h3>
                    <p className="p-founder-bio">{f.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FOOTER STRIP */}
        <div className="p-footer-strip">
          <h3>Uma experiência Orion nunca é apenas um destino.</h3>
          <p>
            Cada roteiro é desenhado individualmente. Transfer aeroporto-hotel-aeroporto com
            motorista bilíngue, seguro-viagem incluído com cobertura ampliada, hospedagem em
            área bem localizada e um consultor Orion acompanhando você antes, durante e depois.
          </p>
          <div className="p-signature">
            <strong>Viagens Orion</strong> · Credenciada pelo Ministério do Turismo · Cadastur
            <div className="p-signature-legal">
              S R Scabio Operadora e Agência de Viagens - ME
              <br />
              <span>CNPJ 37.476.039/0001-90</span>
            </div>
          </div>
        </div>

        {/* FOOTER — Copyright, contact, política */}
        <footer className="p-portfolio-footer" data-testid="p-portfolio-footer">
          <div className="p-pf-inner">
            <div className="p-pf-brand">
              <div className="p-pf-brand-name">Viagens Orion</div>
              <div className="p-pf-brand-tag">Agência & Operadora de Turismo · Cadastur</div>
            </div>
            <div className="p-pf-contact">
              <span className="p-pf-label">Contato</span>
              <a href="mailto:contato@viagensorion.com" className="p-pf-link" data-testid="p-pf-email">
                contato@viagensorion.com
              </a>
            </div>
            <div className="p-pf-links">
              <a href="/politica-de-privacidade" className="p-pf-link" target="_blank" rel="noopener" data-testid="p-pf-privacy">
                Política de Privacidade
              </a>
              <a href="/termos" className="p-pf-link" target="_blank" rel="noopener" data-testid="p-pf-terms">
                Termos de Uso
              </a>
            </div>
          </div>
          <div className="p-pf-copy">
            © {new Date().getFullYear()} Viagens Orion. Todos os direitos reservados.
          </div>
        </footer>
      </div>

      {/* QR MODAL */}
      {showQR && (
        <div className="p-modal" onClick={() => setShowQR(false)} data-testid="p-qr-modal">
          <div className="p-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="p-modal-close" onClick={() => setShowQR(false)} aria-label="Fechar">×</button>
            <h3 className="p-modal-title">Portfólio Orion — QR</h3>
            <p className="p-modal-sub">Aponte a câmera. Ideal para materiais impressos, cartões de visita e recepção.</p>
            <div className="p-qr-wrap">
              <QRCodeSVG value={portfolioUrl} size={256} level="M" bgColor="#ffffff" fgColor="#05010f" />
            </div>
            <div className="p-qr-url">{portfolioUrl}</div>
            <div className="p-modal-actions">
              <button className="p-modal-btn p-modal-btn-primary" onClick={downloadQR} data-testid="p-qr-download">
                Baixar PNG
              </button>
              <button className="p-modal-btn p-modal-btn-ghost" onClick={() => { navigator.clipboard?.writeText(portfolioUrl); }}>
                Copiar link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DISCLAIMER MODAL */}
      {showDisclaimer && (
        <div className="p-modal" onClick={() => setShowDisclaimer(false)} data-testid="p-disclaimer-modal">
          <div className="p-modal-card" style={{ textAlign: "left", maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <button className="p-modal-close" onClick={() => setShowDisclaimer(false)} aria-label="Fechar">×</button>
            <h3 className="p-modal-title" style={{ textAlign: "left" }}>O que está incluído no valor</h3>
            <p className="p-modal-sub" style={{ textAlign: "left" }}>
              Os valores exibidos são <strong>uma média por pessoa</strong> e servem como referência inicial. O pacote-base inclui:
            </p>
            <ul style={{ textAlign: "left", padding: "0 0 0 20px", color: "var(--p-text-dim)", fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
              <li><span style={{ color: "var(--p-gold)" }}>✈</span> Passagem ida/volta (aéreo) ou ônibus-leito premium (rodoviário)</li>
              <li><span style={{ color: "var(--p-gold)" }}>✚</span> Transfer IN/OUT (aeroporto/rodoviária ↔ hotel ↔ retorno)</li>
              <li><span style={{ color: "var(--p-gold)" }}>🌐</span> Motorista bilíngue durante os transfers</li>
              <li><span style={{ color: "var(--p-gold)" }}>🛡</span> Seguro-viagem com cobertura ampliada</li>
              <li><span style={{ color: "var(--p-gold)" }}>◈</span> Hospedagem bem localizada <em style={{color:"var(--p-text-dim)", fontSize:12}}>(exceto pacotes bate-volta, marcados como "1 dia")</em></li>
            </ul>
            <p className="p-modal-sub" style={{ textAlign: "left", margin: 0 }}>
              <strong style={{ color: "var(--p-cyan)" }}>Passeios e atrações são opcionais</strong> e podem ser adicionados via agências parceiras da Orion na cotação personalizada. Valores para destinos rodoviários seguem a tabela oficial da loja Orion.
            </p>
          </div>
        </div>
      )}

      {/* PRESENTATION MODE (Fullscreen slideshow — F key) */}
      {presIdx >= 0 && filteredSections[presIdx] && (
        <div className="p-presentation" data-testid="p-presentation">
          <div className="p-pres-bg" aria-hidden>
            {filteredSections[presIdx].items[0]?.image && (
              <img key={filteredSections[presIdx].id} src={filteredSections[presIdx].items[0].image} alt="" loading="lazy" decoding="async" />
            )}
          </div>
          <div className="p-pres-content">
            <header className="p-pres-header">
              <div>
                <div className="p-pres-eyebrow">
                  {presIdx + 1} de {filteredSections.length} · Viagens Orion
                </div>
                <h2 className="p-pres-title">
                  {(() => {
                    const t = filteredSections[presIdx].title;
                    const [a, b] = t.includes(" · ") ? t.split(" · ") : [t, null];
                    return <>{a}{b && <em> · {b}</em>}</>;
                  })()}
                </h2>
              </div>
              <div className="p-pres-count">{filteredSections[presIdx].count}</div>
            </header>

            <div className="p-pres-cards">
              <div className="p-pres-grid" key={filteredSections[presIdx].id}>
                {filteredSections[presIdx].items.map((it) => (
                  <DestinationCard
                    key={it.id}
                    item={it}
                    clientMode={clientMode}
                    isSaved={savedSet.has(it.id)}
                    onToggleSave={toggleSave}
                    showCorpBadge={filteredSections[presIdx].id === "corporativos"}
                    showCorpSummary={filteredSections[presIdx].id === "corporativos"}
                  />
                ))}
              </div>
            </div>

            <footer className="p-pres-footer">
              <div className="p-pres-nav">
                <button className="p-pres-arrow" onClick={prevSlide} disabled={presIdx === 0} aria-label="Anterior" data-testid="p-pres-prev">◀</button>
                <button className="p-pres-arrow" onClick={nextSlide} disabled={presIdx >= filteredSections.length - 1} aria-label="Próxima" data-testid="p-pres-next">▶</button>
              </div>
              <div className="p-pres-progress">
                <div className="p-pres-progress-fill" style={{ width: `${((presIdx + 1) / filteredSections.length) * 100}%` }} />
              </div>
              <div>
                <span style={{ marginRight: 16 }}>← → navegar</span>
                <span>F ou Esc · sair</span>
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* Corporate Quote Modal */}
      {quoteOpen && (
        <CorporateQuoteModal
          onClose={() => setQuoteOpen(false)}
          destinations={allDestinations}
          savedIds={savedIds}
          initial={quoteInitial}
        />
      )}

      {/* Floating Quote CTA — appears when user favorites 1+ trips */}
      {savedIds.length >= 1 && !quoteOpen && (
        <div className="p-floating-quote" data-testid="p-floating-quote">
          <span className="p-fq-hint">
            Receba <strong>até 40% de desconto</strong> para grupos a partir de 10 pessoas
          </span>
          <button
            className="p-fq-btn"
            onClick={() => openQuote({ preselectIds: savedIds })}
            data-testid="p-floating-quote-btn"
          >
            <span>Solicitar Cotação</span>
            <span className="p-fq-arrow">→</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * CorporateQuoteModal
 * Multi-step-free form to build a corporate quote request and dispatch to WhatsApp.
 * - Multi-select destinations with search (lupa)
 * - Preselect from user context (whole "Minha Lista" or a single card)
 * - Params: participantes, mês, orçamento por pessoa, empresa, nome, tipo (Incentivo/Negócios)
 */
export function CorporateQuoteModal({ onClose, destinations, savedIds, initial }) {
  const preselectIds = initial?.preselectIds || [];
  const [selected, setSelected] = useState(() => new Set(preselectIds));
  const [search, setSearch] = useState("");
  const [showList, setShowList] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [participantes, setParticipantes] = useState(10);
  const [mes, setMes] = useState("");
  const [orcamento, setOrcamento] = useState("");
  const [tipo, setTipo] = useState(initial?.focusCorp ? "incentivo" : "");
  const [obs, setObs] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = destinations.map((d) => ({
      id: d.id,
      name: d.name.replace(/\s+[A-Z]{2}\s*$/, ""),
      region: d.region || "",
      corp_type: d.corp_type || null,
    }));
    if (!q) return list.slice(0, 60);
    return list
      .filter((d) => d.name.toLowerCase().includes(q) || d.region.toLowerCase().includes(q))
      .slice(0, 60);
  }, [destinations, search]);

  const selectedItems = useMemo(
    () => destinations.filter((d) => selected.has(d.id)).map((d) => ({
      id: d.id,
      name: d.name.replace(/\s+[A-Z]{2}\s*$/, ""),
      price_from: d.price_from,
      days: d.days,
      corp_type: d.corp_type,
    })),
    [destinations, selected]
  );

  const toggleId = useCallback((id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);
  const addAllSaved = useCallback(() => {
    setSelected((prev) => {
      const s = new Set(prev);
      for (const id of savedIds) s.add(id);
      return s;
    });
  }, [savedIds]);
  const clearAll = useCallback(() => setSelected(new Set()), []);

  const TIPOS = [
    { id: "incentivo", label: "Incentivo · Premiação de Metas" },
    { id: "negocios",  label: "Negócios · Feiras / Convenções" },
    { id: "bleisure",  label: "Bleisure · Convenção + Lazer" },
    { id: "outro",     label: "Ainda não sei · quero orientação" },
  ];

  const MESES = [
    "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
    "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
    "Ainda em definição",
  ];

  const canSend = selectedItems.length > 0 && participantes > 0 && (name.trim() || company.trim());

  const handleSend = () => {
    if (!canSend) return;
    const destLines = selectedItems.map((d) => {
      const preco = d.price_from ? ` — a partir de R$ ${d.price_from.toLocaleString("pt-BR")}/pessoa` : "";
      return `• ${d.name}${preco}`;
    });
    const tipoLabel = TIPOS.find((t) => t.id === tipo)?.label || "A definir";
    const orc = orcamento ? `R$ ${Number(orcamento).toLocaleString("pt-BR")}/pessoa` : "A definir";
    const totalLine = orcamento && participantes
      ? `\n*Orçamento total estimado:* R$ ${(Number(orcamento) * Number(participantes)).toLocaleString("pt-BR")}`
      : "";
    const text = [
      "Olá, Orion!",
      "",
      "*Solicitação de Cotação Corporativa*",
      "",
      `*Contato:* ${name || "—"}`,
      `*Empresa:* ${company || "—"}`,
      `*Tipo de viagem:* ${tipoLabel}`,
      `*Participantes:* ${participantes} pessoa(s)`,
      `*Período desejado:* ${mes || "A definir"}`,
      `*Orçamento por pessoa:* ${orc}${totalLine}`,
      "",
      "*Destinos de interesse:*",
      ...destLines,
      "",
      obs ? `*Observações:* ${obs}` : "",
      "",
      "Podem me enviar as opções e valores personalizados?",
      "(Grupos a partir de 10 pessoas têm até 60% de desconto sobre os valores estimados)",
    ].filter(Boolean).join("\n");
    const whatsappNumber = "5511920043024";
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="p-quote-overlay" onClick={onClose} data-testid="p-quote-modal">
      <div className="p-quote-modal" onClick={(e) => e.stopPropagation()}>
        <button className="p-quote-close" onClick={onClose} aria-label="Fechar" data-testid="p-quote-close">×</button>
        <header className="p-quote-head">
          <div className="p-quote-eyebrow">◈ Cotação Corporativa</div>
          <h2 className="p-quote-title">Solicitar Cotação para Grupos & Empresas</h2>
          <p className="p-quote-sub">
            Monte o pedido em 30 segundos — vamos responder com opções personalizadas.
            <br />
            <em>Grupos a partir de 10 pessoas têm até 60% de desconto sobre os valores estimados.</em>
          </p>
        </header>

        <div className="p-quote-body">
          {/* Left column: contact + params */}
          <div className="p-quote-col">
            <div className="p-quote-row">
              <label>
                <span>Seu nome</span>
                <input
                  data-testid="p-quote-name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Como devemos te chamar?"
                />
              </label>
              <label>
                <span>Empresa</span>
                <input
                  data-testid="p-quote-company"
                  value={company} onChange={(e) => setCompany(e.target.value)}
                  placeholder="Ex: Acme S.A."
                />
              </label>
            </div>
            <div className="p-quote-row">
              <label>
                <span>Nº de participantes</span>
                <input
                  data-testid="p-quote-participants"
                  type="number" min="1" step="1"
                  value={participantes} onChange={(e) => setParticipantes(Number(e.target.value))}
                />
              </label>
              <label>
                <span>Mês desejado</span>
                <select data-testid="p-quote-month" value={mes} onChange={(e) => setMes(e.target.value)}>
                  <option value="">Selecione…</option>
                  {MESES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            </div>
            <div className="p-quote-row">
              <label>
                <span>Orçamento por pessoa (R$)</span>
                <input
                  data-testid="p-quote-budget"
                  type="number" min="0" step="500"
                  value={orcamento} onChange={(e) => setOrcamento(e.target.value)}
                  placeholder="Ex: 6000"
                />
              </label>
              <label>
                <span>Tipo de viagem</span>
                <select data-testid="p-quote-type" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="">Selecione…</option>
                  {TIPOS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </label>
            </div>
            <label className="p-quote-full">
              <span>Observações (opcional)</span>
              <textarea
                data-testid="p-quote-obs"
                value={obs} onChange={(e) => setObs(e.target.value)}
                rows={3}
                placeholder="Ex: precisamos de sala de reunião, restrições alimentares, datas específicas…"
              />
            </label>
          </div>

          {/* Right column: destinations picker */}
          <div className="p-quote-col p-quote-picker">
            <div className="p-quote-picker-head">
              <div className="p-quote-picker-title">
                Destinos de interesse
                <span className="p-quote-picker-count">{selected.size} selecionado(s)</span>
              </div>
              <div className="p-quote-picker-actions">
                {savedIds.length > 0 && (
                  <button
                    className="p-quote-mini-btn"
                    onClick={addAllSaved}
                    data-testid="p-quote-add-saved"
                  >
                    ★ Adicionar Minha Lista ({savedIds.length})
                  </button>
                )}
                {selected.size > 0 && (
                  <button className="p-quote-mini-btn ghost" onClick={clearAll} data-testid="p-quote-clear">
                    Limpar
                  </button>
                )}
              </div>
            </div>

            <div className="p-quote-search">
              <span className="p-quote-search-icon">🔍</span>
              <input
                data-testid="p-quote-search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowList(true); }}
                onFocus={() => setShowList(true)}
                placeholder="Buscar destino (ex: lisboa, cancun, nova york…)"
              />
              {search && (
                <button
                  className="p-quote-search-clear"
                  onClick={() => { setSearch(""); }}
                  aria-label="Limpar busca"
                >×</button>
              )}
            </div>

            {selected.size > 0 && (
              <div className="p-quote-chips" data-testid="p-quote-chips">
                {selectedItems.map((d) => (
                  <button
                    key={d.id}
                    className="p-quote-chip"
                    onClick={() => toggleId(d.id)}
                    aria-label={`Remover ${d.name}`}
                    title="Remover"
                  >
                    <span>{d.name}</span>
                    <span aria-hidden>×</span>
                  </button>
                ))}
              </div>
            )}

            {!showList && !search ? (
              <button
                className="p-quote-list-toggle"
                onClick={() => setShowList(true)}
                data-testid="p-quote-list-open"
              >
                Ver todos os destinos ({destinations.length}) ↓
              </button>
            ) : (
              <div className="p-quote-list" data-testid="p-quote-list">
                {filtered.length === 0 ? (
                  <div className="p-quote-empty">Nenhum destino encontrado para "{search}".</div>
                ) : filtered.map((d) => {
                  const isSel = selected.has(d.id);
                  return (
                    <label key={d.id} className={`p-quote-item ${isSel ? "selected" : ""}`}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleId(d.id)} />
                      <span className="p-quote-item-name">{d.name}</span>
                      {d.corp_type && (
                        <span className={`p-quote-item-badge tag-${d.corp_type}`}>
                          {d.corp_type === "negocios" ? "Negócios" :
                           d.corp_type === "incentivo" ? "Incentivo" : "Bleisure"}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <footer className="p-quote-foot">
          <div className="p-quote-foot-note">
            Ao enviar, abriremos o WhatsApp com sua solicitação estruturada. Nenhum dado é armazenado.
          </div>
          <div className="p-quote-foot-actions">
            <button className="p-quote-btn ghost" onClick={onClose} data-testid="p-quote-cancel">Cancelar</button>
            <button
              className="p-quote-btn primary"
              disabled={!canSend}
              onClick={handleSend}
              data-testid="p-quote-send"
            >
              Enviar via WhatsApp →
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
